import { useToast } from '@airship-platform/airship-patterns';
import {
  PercentageSection,
  PriceSectionEditable,
  ProductCard,
  useProducts,
} from '@airship-platform/airship-proposal';
import { FlexBox, SectionCard } from '@airship-platform/jam-web/components';
import {
  DefaultRebateIcon,
  RebateIcon,
  TrashIcon,
} from '@airship-platform/jam-web/icons';
import IconButton from '@mui/material/IconButton';
import {
  AppointmentProposalOptionModifier,
  useAppointmentProposalOptionModifiers,
} from '../../data-access/appointmentProposalOptionModifiers';
import { useAppointmentProposalOptions } from '../../data-access/appointmentProposalOptions';
import { OfflineSyncErrorChip } from '../../patterns';
import AddRebateButton from './components/AddRebateButton';

const OptionModifiers: React.FC = () => {
  const { currentOption } = useAppointmentProposalOptions();
  const {
    proposalModifiers,
    isLoading,
    deleteAppointmentProposalOptionModifier,
  } = useAppointmentProposalOptionModifiers();
  const { availableRebates } = useProducts();
  const { showToast } = useToast();

  if (!currentOption || !availableRebates.length) {
    return null;
  }

  const isReadOnly = currentOption.isReadOnly;

  const modifiers = proposalModifiers.filter(
    (pm) =>
      pm.appointmentProposalOptionId ===
        currentOption.appointmentProposalOptionId &&
      pm.modifierType === 'REBATE'
  );

  const hasItems = !!modifiers.length;

  const handleDelete = (modifier: AppointmentProposalOptionModifier) =>
    deleteAppointmentProposalOptionModifier(
      modifier.appointmentId,
      modifier.appointmentProposalId,
      modifier.appointmentProposalOptionId,
      modifier.appointmentProposalOptionModifierId
    ).then(() =>
      showToast(`Rebate ${modifier.displayName} removed successfully`, 'info')
    );

  return (
    <SectionCard
      title="Add rebates"
      icon={
        <RebateIcon width={hasItems ? 70 : 100} height={hasItems ? 70 : 100} />
      }
      hasItems={hasItems}
      to="query-modifiers"
      action={<AddRebateButton variant={hasItems ? 'button' : 'iconButton'} />}
    >
      {modifiers.map((modifier) => (
        <FlexBox
          key={modifier.appointmentProposalOptionModifierId}
          alignItems="center"
          justifyContent="space-between"
          gap={3}
          py={2}
        >
          <ProductCard
            product={{
              images: modifier.images,
              name: modifier.displayName || '',
              description: modifier.description,
            }}
            noImageIcon={<DefaultRebateIcon />}
            productNameSuffix={
              <OfflineSyncErrorChip errors={modifier.offlineSyncErrors} />
            }
            priceSection={
              modifier.amountType === 'PERCENTAGE' ? (
                <PercentageSection value={modifier.amount * 100} prefix="-" />
              ) : (
                <PriceSectionEditable
                  defaultPriceType="STANDARD"
                  prices={[
                    {
                      value: modifier.amount,
                      currency: 'USD',
                      priceType: 'STANDARD',
                      taxable: modifier.taxable,
                    },
                  ]}
                  startAdornment="-"
                />
              )
            }
            productClickRoute={`modifier/${modifier.appointmentProposalOptionModifierId}`}
          />
          <IconButton
            color="softGold"
            onClick={() => handleDelete(modifier)}
            disabled={isLoading || isReadOnly}
          >
            <TrashIcon />
          </IconButton>
        </FlexBox>
      ))}
    </SectionCard>
  );
};

export default OptionModifiers;
